import {
  Box,
  Checkbox,
  CloseButton,
  Divider,
  Flex,
  Link,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";

import { useEntityLink } from "../../../../shared/hooks/useEntityLink";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { getFullName } from "../../../../shared/utils/get-full-name";
import {
  getRegionLabel,
  MappableRnSchedulingMapPatient,
  SCHEDULING_STATUS_COLOR_SCHEME,
  SCHEDULING_STATUS_LABEL,
} from "../rn-scheduling-map.types";

type Props = {
  patient: MappableRnSchedulingMapPatient;
  isChecked: boolean;
  onClose: () => void;
  onToggleChecked: () => void;
};

export default function RnSchedulingMapPatientPopup(props: Props) {
  const entityLink = useEntityLink();

  function handleOpenPatient(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    event.stopPropagation();
    entityLink.open({ event, entity: { id: props.patient.patientId as never, type: "Patient" } });
  }

  return (
    <Box minWidth="260px">
      <Flex align="flex-start" gap={2} justify="space-between">
        <Flex align="center" gap={2} minWidth={0}>
          <Checkbox
            aria-label={`Select ${getFullName(props.patient)}`}
            isChecked={props.isChecked}
            onChange={props.onToggleChecked}
          />
          <Link color="blue.600" fontWeight="semibold" onClick={handleOpenPatient}>
            {getFullName(props.patient)}
          </Link>
        </Flex>
        <CloseButton size="sm" onClick={props.onClose} />
      </Flex>

      <Divider my={2} />

      <VStack align="stretch" spacing={1}>
        <DetailRow
          label="Cert. period ends"
          value={dateFormatter.toDate(props.patient.certificationPeriodEndDate)}
        />
        <DetailRow label="Type" value={humanizeCertificationPeriodType(props.patient)} />
        <DetailRow label="Office" value={props.patient.officeName} />
        <DetailRow
          label="Borough / Region"
          value={props.patient.countyName !== null ? getRegionLabel(props.patient.countyName) : "-"}
        />
        <DetailRow
          label="Contracts"
          value={
            props.patient.contractTypeNames.length > 0
              ? props.patient.contractTypeNames.join(", ")
              : "-"
          }
        />
        <DetailRow label="Address" value={props.patient.address ?? "-"} />
        {props.patient.phoneNumber !== null && (
          <DetailRow label="Phone" value={props.patient.phoneNumber} />
        )}
        <DetailRow label="Assigned RN" value={props.patient.assignedRnName ?? "Not assigned"} />
        {props.patient.scheduledVisitDateTime !== null && (
          <DetailRow
            label="Next scheduled visit"
            value={dateFormatter.toDateTime(props.patient.scheduledVisitDateTime)}
          />
        )}
        {props.patient.scheduledVisitCaregiverName !== null && (
          <DetailRow label="Scheduled with" value={props.patient.scheduledVisitCaregiverName} />
        )}
        {props.patient.completedVisitDateTime !== null && (
          <DetailRow
            label="Visit completed on"
            value={dateFormatter.toDateTime(props.patient.completedVisitDateTime)}
          />
        )}
        {props.patient.completedVisitCaregiverName !== null && (
          <DetailRow label="Visited by" value={props.patient.completedVisitCaregiverName} />
        )}
      </VStack>

      <Box mt={2}>
        <Tag colorScheme={SCHEDULING_STATUS_COLOR_SCHEME[props.patient.schedulingStatus]} size="sm">
          {SCHEDULING_STATUS_LABEL[props.patient.schedulingStatus]}
        </Tag>
      </Box>
    </Box>
  );
}

function DetailRow(props: { label: string; value: string }) {
  return (
    <Flex fontSize="xs" gap={2} justify="space-between">
      <Text color="gray.500" flexShrink={0}>
        {props.label}
      </Text>
      <Text textAlign="right">{props.value}</Text>
    </Flex>
  );
}

function humanizeCertificationPeriodType(patient: MappableRnSchedulingMapPatient): string {
  switch (patient.certificationPeriodType) {
    case "START_OF_CARE":
      return "Start of care";
    case "REASSESSMENT":
      return "Reassessment";
  }
}
